(function () {
  const DESKTOP_MQL = window.matchMedia('(min-width: 750px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const instances = [];
  const DRAG_CLICK_THRESHOLD = 5;

  function initGalleryDots(layout) {
    const viewer = layout.querySelector('[id^="GalleryViewer-"]');
    const dotsRoot = layout.querySelector('.oshe-pdp-gallery-dots');
    if (!viewer || !dotsRoot || dotsRoot.dataset.osheDotsBound === 'true') return;
    dotsRoot.dataset.osheDotsBound = 'true';

    const dots = [...dotsRoot.querySelectorAll('.oshe-pdp-gallery-dots__dot')];
    const slider = viewer.querySelector('[id^="Slider-Gallery-"]');
    if (!dots.length || !slider) return;

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    };

    viewer.addEventListener('slideChanged', (event) => {
      const page = event.detail?.currentPage;
      if (page) setActive(page - 1);
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.slideIndex);
        if (Number.isNaN(index)) return;
        const slides = [...slider.querySelectorAll('.slider__slide')].filter((el) => el.clientWidth > 0);
        const target = slides[index];
        if (!target) return;
        slider.scrollTo({ left: target.offsetLeft, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
        setActive(index);
      });
    });
  }

  function OshePdpTray(clip, layout) {
    this.clip = clip;
    this.layout = layout;
    this.inner = clip.querySelector('.oshe-pdp-tray-inner');
    this.handle = clip.querySelector('.oshe-pdp-tray-handle__button');
    this.handleWrap = clip.querySelector('.oshe-pdp-tray-handle');
    this.blocks = clip.querySelector('.oshe-pdp-blocks');
    this.mediaWrapper = layout.querySelector('.product__media-wrapper');

    this.expanded = false;
    this.translateY = 0;
    this.peekHeight = 160;
    this.expandOffset = 280;

    this.dragging = false;
    this.dragDistance = 0;
    this.startClientY = 0;
    this.startTranslateY = 0;
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onResize = this.onResize.bind(this);
    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.measureTimer = null;
  }

  OshePdpTray.prototype.measurePeek = function () {
    const hadExpanded = this.inner.classList.contains('is-expanded');
    this.inner.classList.add('is-expanded');
    const prevTransform = this.inner.style.transform;
    this.inner.style.transform = 'translate3d(0, 0, 0)';
    const fullContent = this.inner.scrollHeight;
    if (!hadExpanded) this.inner.classList.remove('is-expanded');
    this.inner.style.transform = prevTransform;

    const handle = this.clip.querySelector('.oshe-pdp-tray-handle');
    const title = this.blocks.querySelector('.oshe-pdp-block--title');
    const price = this.blocks.querySelector('.oshe-pdp-block--price');
    const buy = this.blocks.querySelector('.oshe-pdp-block--buy_buttons');
    let peek = 0;
    [handle, title, price, buy].forEach((el) => {
      if (el) peek += el.offsetHeight;
    });
    this.peekHeight = Math.max(Math.round(peek + 4), 140);

    const mediaH = this.mediaWrapper ? this.mediaWrapper.clientHeight : 400;
    const contentExtra = Math.max(0, fullContent - this.peekHeight);
    const overMedia = Math.max(0, mediaH - this.peekHeight * 0.3);
    const viewportCap = Math.max(0, window.innerHeight * 0.75 - this.peekHeight);

    this.expandOffset = Math.round(Math.min(overMedia, contentExtra, viewportCap));
    this.clip.style.setProperty('--oshe-tray-peek', `${this.peekHeight}px`);
  };

  OshePdpTray.prototype.applyTransform = function (y, options = {}) {
    if (this.expanded) return;
    const animate = options.animate === true && !this.inner.classList.contains('is-dragging');
    if (animate && !REDUCED_MOTION.matches) {
      this.inner.style.transition = '';
    } else if (!this.inner.classList.contains('is-dragging')) {
      this.inner.style.transition = 'none';
      requestAnimationFrame(() => {
        if (!this.inner.classList.contains('is-dragging')) this.inner.style.transition = '';
      });
    }
    this.translateY = y;
    const lift = Math.abs(Math.min(0, y));
    this.clip.style.setProperty('--oshe-tray-lift', `${Math.round(lift)}px`);
    this.inner.classList.toggle('is-raised', lift > 8 && !this.expanded);
    this.inner.style.transform = `translate3d(0, ${Math.round(y)}px, 0)`;
  };

  OshePdpTray.prototype.applyExpandedState = function (expanded, skipTransform) {
    this.expanded = expanded;
    this.inner.dataset.expanded = expanded ? 'true' : 'false';
    this.inner.classList.toggle('is-expanded', expanded);
    this.clip.classList.toggle('is-open', expanded);
    if (!expanded) this.inner.classList.remove('is-raised');
    if (this.handle) this.handle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      this.inner.style.transform = 'none';
      this.clip.style.setProperty('--oshe-tray-lift', '0px');
    } else if (!skipTransform) {
      this.applyTransform(0, { animate: true });
    }
  };

  OshePdpTray.prototype.setExpanded = function (expanded) {
    this.applyExpandedState(expanded);
    this.translateY = expanded ? -this.expandOffset : 0;
  };

  OshePdpTray.prototype.toggle = function () {
    this.setExpanded(!this.expanded);
  };

  OshePdpTray.prototype.isPeekDragTarget = function (target) {
    if (!target || !this.inner.contains(target)) return false;
    if (
      target.closest(
        '.oshe-pdp-block--variant_picker, .oshe-pdp-block--installment, .oshe-pdp-block--description, .oshe-pdp-block--collapsible_tab'
      )
    ) {
      return false;
    }
    return !!target.closest(
      '.oshe-pdp-tray-handle, .oshe-pdp-block--title, .oshe-pdp-block--price, .oshe-pdp-block--buy_buttons'
    );
  };

  OshePdpTray.prototype.onPointerDown = function (event) {
    if (REDUCED_MOTION.matches) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!this.isPeekDragTarget(event.target)) return;
    if (event.target.closest('a, input, select, textarea, label')) return;
    if (event.target.closest('button:not(.oshe-pdp-tray-handle__button)')) return;

    this.dragging = true;
    this.dragDistance = 0;
    this.startClientY = event.clientY;
    this.startTranslateY = this.translateY;
    this.inner.classList.add('is-dragging');
    (this.inner).setPointerCapture(event.pointerId);
    document.addEventListener('pointermove', this.onPointerMove);
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointercancel', this.onPointerUp);
  };

  OshePdpTray.prototype.onPointerMove = function (event) {
    if (!this.dragging) return;
    const delta = this.startClientY - event.clientY;
    this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));
    const nextY = Math.min(0, Math.max(-this.expandOffset, this.startTranslateY - delta));
    this.applyTransform(nextY, { animate: false });
  };

  OshePdpTray.prototype.onPointerUp = function () {
    if (!this.dragging) return;
    this.dragging = false;
    this.inner.classList.remove('is-dragging');
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerUp);

    const threshold = -this.expandOffset * 0.35;
    const shouldExpand = this.translateY <= threshold;
    if (shouldExpand) {
      this.translateY = -this.expandOffset;
    }
    this.setExpanded(shouldExpand);
  };

  OshePdpTray.prototype.scheduleMeasure = function () {
    if (this.measureTimer) clearTimeout(this.measureTimer);
    this.measureTimer = setTimeout(() => {
      this.measureTimer = null;
      this.measure();
    }, 100);
  };

  OshePdpTray.prototype.measure = function () {
    this.measurePeek();
    if (this.expanded) {
      this.inner.style.transform = 'none';
      this.clip.classList.add('is-open');
    } else {
      this.applyTransform(0, { animate: false });
    }
  };

  OshePdpTray.prototype.bind = function () {
    if (this.handle) {
      this.handle.addEventListener('click', (e) => {
        if (this.dragDistance > DRAG_CLICK_THRESHOLD) {
          this.dragDistance = 0;
          return;
        }
        e.preventDefault();
        this.toggle();
      });
    }

    const dragTarget = this.inner;
    if (dragTarget && !REDUCED_MOTION.matches) {
      dragTarget.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    }

    if (typeof ResizeObserver !== 'undefined' && this.blocks) {
      this.ro = new ResizeObserver(() => this.scheduleMeasure());
      this.ro.observe(this.blocks);
      if (this.mediaWrapper) this.ro.observe(this.mediaWrapper);
    }

    window.addEventListener('resize', this.onResize);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this.scheduleMeasure();
      });
    }

    this.measure();
    this.applyExpandedState(false, true);
    this.applyTransform(0, { animate: false });
  };

  OshePdpTray.prototype.destroy = function () {
    window.removeEventListener('resize', this.onResize);
    if (this.measureTimer) clearTimeout(this.measureTimer);
    if (this.ro) this.ro.disconnect();
    this.clip.style.removeProperty('--oshe-tray-peek');
    this.clip.style.removeProperty('--oshe-tray-lift');
    this.clip.classList.remove('is-open');
    this.inner.style.transform = '';
    this.inner.style.minHeight = '';
    this.inner.dataset.expanded = 'false';
    this.inner.classList.remove('is-expanded', 'is-dragging', 'is-raised');
  };

  OshePdpTray.prototype.onResize = function () {
    if (DESKTOP_MQL.matches) return;
    this.scheduleMeasure();
  };

  function destroyAll() {
    while (instances.length) {
      instances.pop().destroy();
    }
  }

  function initMobile() {
    destroyAll();
    if (DESKTOP_MQL.matches) return;

    document.querySelectorAll('.oshe-product-layout').forEach((layout) => {
      initGalleryDots(layout);
    });
  }

  function onBreakpoint() {
    if (DESKTOP_MQL.matches) {
      destroyAll();
    } else {
      initMobile();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onBreakpoint);
  } else {
    onBreakpoint();
  }

  DESKTOP_MQL.addEventListener('change', onBreakpoint);
})();
